/* @flow */
import idx from 'idx';
import cx from 'classnames';
import * as React from 'react';
import { graphql } from 'react-apollo';
import Emoji from 'components/Emoji';
import CurrentUser from 'auth/CurrentUser';
import Tooltip from 'components/Tooltip';
import LayerTrigger from 'components/LayerTrigger';
import reactionSettings from 'user/queries/reactionSettings.gql';

import './styles/MessageReaction.scss';

type Props = {
  reaction: ReactionGQL,
  addReactionToSummary: Function,
  disableReactionToggle?: boolean,
  removeReactionFromSummary: Function,
  reactionInventory?: Map<string, string>,
};

const MAX_NAMES = 10;

const replaceYourNameWithTheWordYouIn = names => {
  return names
    .map(name => {
      return name === CurrentUser.getUser().getName() ? 'You' : name;
    })
    .sort((a, b) => {
      if (a === 'You') {
        return -1;
      }

      if (b === 'You') {
        return 1;
      }

      return a.localeCompare(b);
    });
};

export const makeListy = (names: string[], maxNamed: number = MAX_NAMES): React.Element => {
  const toName = names.slice(0, maxNamed).map(n => <li key={n}>{n}</li>);

  const remainingCount = names.length - maxNamed;

  if (remainingCount > 0) {
    return [...toName, <li key="&rest">{`& ${remainingCount} more...`}</li>];
  }

  return toName;
};

export class MessageReaction extends React.Component<Props> {
  hasBeenSelectedByCurrentUser() {
    return this.props.reaction.users.some(u => u.uuid === CurrentUser.getUserUuid());
  }

  toggleReaction = () => {
    const { reaction, disableReactionToggle } = this.props;

    if (disableReactionToggle) {
      return;
    }

    if (this.hasBeenSelectedByCurrentUser()) {
      this.props.removeReactionFromSummary(reaction.name);
    } else {
      this.props.addReactionToSummary(reaction.name);
    }
  };

  render() {
    const { reaction, reactionInventory } = this.props;

    if (!reactionInventory) {
      return null;
    }

    const unicodeChar = reactionInventory.get(reaction.name);

    if (!unicodeChar) {
      return null;
    }

    const tooltip = (
      <Tooltip>
        <ul>{makeListy(replaceYourNameWithTheWordYouIn(reaction.users.map(u => u.name)))}</ul>
      </Tooltip>
    );

    return (
      <LayerTrigger trigger={['hover', 'focus']} layer={tooltip} placement="bottom">
        <div
          className={cx('MessageReaction', {
            'MessageReaction-selfReacted': this.hasBeenSelectedByCurrentUser(),
          })}
          onClick={this.toggleReaction}>
          <Emoji className="MessageReaction-emoji" character={unicodeChar} />
          {reaction.count}
        </div>
      </LayerTrigger>
    );
  }
}

const withReactions = graphql(reactionSettings, {
  props: ({ data }) => {
    const reactions = idx(data, _ => _.settings.reactionInventory.reactions);
    if (!reactions) {
      return new Map();
    }

    return {
      reactionInventory: new Map(reactions.map(r => [r.name, r.unicode])),
    };
  },
});

export default withReactions(MessageReaction);
